import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AGUAppModuleAccent, AGUAppModuleDefinition, VisibleSiblingsOf } from 'agu_app_shell';

/**
 * The resolved composition, readable by the modules inside it.
 *
 * The member dashboard needs to know its SIBLINGS — it renders one tile per other
 * module in the same app — and only the surface has that answer, because it is
 * the thing that read UIConfigDetails and applied entitlement. This is the seam
 * between them.
 *
 * A service rather than ngComponentOutlet inputs: inputs would force every module
 * to accept a composition parameter it mostly ignores, and modules should be able
 * to opt in to knowing about their siblings rather than being obliged to.
 *
 * Provided by RaSurfaceModule, so it is per-injector — two Research Accelerator
 * tabs open at once each see their own composition.
 */
@Injectable()
export class RaCompositionState {
    private modules: readonly AGUAppModuleDefinition[] = [];
    private visitRecorded = false;
    private readonly surfaceReentered = new Subject<void>();

    /**
     * Emits when the member comes back to this surface after MJ detached it (another app tab in
     * between) — see `RaSurfaceComponent.OnSurfaceReentered`. Emitted AFTER the visit flag is reset
     * and the session re-checked, so a module reloading on it records a fresh visit.
     */
    readonly SurfaceReentered$: Observable<void> = this.surfaceReentered.asObservable();

    /** Every module of this app the signed-in user may reach, in nav order. */
    get Modules(): readonly AGUAppModuleDefinition[] {
        return this.modules;
    }

    /** Written once by the surface when the composition resolves. */
    Set(modules: readonly AGUAppModuleDefinition[]): void {
        this.modules = modules;
    }

    /**
     * Siblings of `key` — the dashboard's tile list. Excludes `HiddenFromNav`
     * modules (profile, search) the same way `AGUAppSidebarComponent
     * .VisibleModules` excludes them from the nav rail — a dashboard tile is
     * the nav rail restated as a card, so a module the sidebar hides must not
     * resurface as a tile here. See `VisibleSiblingsOf` for the pure filter
     * and its tests.
     */
    SiblingsOf(key: string): readonly AGUAppModuleDefinition[] {
        return VisibleSiblingsOf(this.modules, key);
    }

    /**
     * The colour `key` paints with — its module definition's own `Accent`.
     *
     * Screens ask the composition rather than importing RA_MODULE_DEFINITIONS
     * directly: that table imports every page component, so a page importing it
     * back would close an evaluation cycle (the table would be built while the
     * page class it names is still in its temporal dead zone). This seam already
     * carries the resolved definitions, and it is the same one the Home
     * dashboard reads its tiles from.
     *
     * Blue — the portal default — when the composition has not resolved yet or
     * does not include `key`; the alternative is a screen that fails to paint.
     */
    AccentOf(key: string): AGUAppModuleAccent {
        return this.modules.find((module) => module.Key === key)?.Accent ?? 'blue';
    }

    /**
     * Whether `ResearchApiService.RecordVisit()` has already run this surface
     * session. The flag survives a switch between modules inside the surface,
     * so moving Home → Grants → Home calls the side-effect-free `Home()` and
     * does not inflate the member's visit count.
     *
     * A "surface session" is NOT the component's lifetime: MJ caches a visited
     * dashboard and re-attaches the same instance when the member comes back
     * from another app (B-3, 2026-10-05 — a probe property survived exit and
     * re-entry), so the surface calls `ResetVisit()` on re-entry and the next
     * Home load records the new visit.
     */
    get VisitRecorded(): boolean {
        return this.visitRecorded;
    }

    /** Marks the one-time `RecordVisit()` call as done for this surface session. */
    MarkVisitRecorded(): void {
        this.visitRecorded = true;
    }

    /** Starts a new surface session on re-entry: the next Home load records a visit again. */
    ResetVisit(): void {
        this.visitRecorded = false;
    }

    /** Tells the mounted modules the member is back — see `SurfaceReentered$`. */
    NotifySurfaceReentered(): void {
        this.surfaceReentered.next();
    }
}
